import React from 'react';
import { Button, Typography, Box } from '@mui/material';

declare global {
  interface WindowEventMap {
    beforeinstallprompt: any;
  }
}

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ may report as Mac
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

const InstallAppButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = React.useState<any | null>(null);
  const [canInstall, setCanInstall] = React.useState(false);
  const [hasInstalled, setHasInstalled] = React.useState(false);
  const [isIOS] = React.useState(() => isIOSDevice());

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setHasInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setHasInstalled(true);
    }
    setDeferredPrompt(null);
    setCanInstall(false);
  };

  // iPhone/iPad: Safari does not fire beforeinstallprompt — show manual steps (no App Store install).
  if (isIOS && !canInstall && !hasInstalled) {
    return (
      <Box sx={{ mt: 1, px: 0.5 }}>
        <Typography variant="caption" display="block" align="center" sx={{ color: 'rgba(0, 0, 0, 0.8)', lineHeight: 1.5 }}>
          <strong>iPhone / iPad:</strong> Open this page in <strong>Safari</strong>, tap{' '}
          <strong>Share</strong>
          {' '}
          <span aria-hidden>(□↑)</span>, then <strong>Add to Home Screen</strong>. There is no separate App Store
          install — Northstar Fuel runs as a web app.
        </Typography>
      </Box>
    );
  }

  if (!canInstall && !hasInstalled) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleClick}
        disabled={!canInstall}
        sx={{
          py: { xs: 1.2, sm: 1.4 },
          fontSize: { xs: 13, sm: 14 },
        }}
      >
        {hasInstalled ? 'App Installed' : 'Add the app to your device'}
      </Button>
      {!hasInstalled && (
        <Typography
          variant="caption"
          display="block"
          align="center"
          sx={{ mt: 0.5, color: 'rgba(0, 0, 0, 0.75)' }}
        >
          Add the app to your home screen for faster access.
        </Typography>
      )}
    </Box>
  );
};

export default InstallAppButton;

